import { dropEmptyArrays, dropEmptyObjects, pushIfNotPresent } from './ScanTemplateLib';
import { ScanTemplateGroup, ScanTemplateRoot, ScanTemplateTask } from './ScanTemplateSchemas';

// ************************************************************************
// Scan Transformers
// ************************************************************************
/*
normalized scan-templates have:
1. all ScanTemplateTask objects moved up to the root ScanTemplateData object
2. all groups simply have an array of taskTemplateIds
3. scanData.taskTemplatesById !== undefined
4. scanData.groupTemplates[*].taskTemplates === undefined
5. scanData.groupTemplates[*].taskTemplateIds !== undefined

de-normalized scan-templates have:
1. all ScanTemplateTask objects moved down to the groupTemplates object (and may exist in multiple groups)
2. all groups have an array of taskTemplates
3. scanData.taskTemplatesById === undefined
4. scanData.groupTemplates[*].taskTemplates !== undefined
5. scanData.groupTemplates[*].taskTemplateIds === undefined

EITHER will detect if called on an already-normalized or de-normalized template or even PARTIALLY normalized or de-normalized template
and do the right thing to avoid any data loss and export a valid normalized or de-normalized template respectively.
*/

const collectInlineTasks = (group: ScanTemplateGroup, taskTemplatesById: Record<string, ScanTemplateTask>): void => {
  group.taskTemplates?.forEach(task => {
    taskTemplatesById[task.id] = task;
  });
};

const validateReferencedTasks = (group: ScanTemplateGroup, taskTemplatesById: Record<string, ScanTemplateTask>): void => {
  group.taskTemplateIds?.forEach(taskId => {
    if (!taskTemplatesById[taskId]) {
      throw new Error(`Task template with ID ${taskId} referenced but not found in taskTemplatesById`);
    }
  });
};

const extractGroupTasks = (group: ScanTemplateGroup, taskTemplatesById: Record<string, ScanTemplateTask>) => {
  const taskTemplateIds: string[] = group.taskTemplateIds ?? [];
  const taskTemplates: ScanTemplateTask[] = group.taskTemplates ?? [];

  group.taskTemplates?.forEach(task => {
    pushIfNotPresent(taskTemplateIds, task.id);
    pushIfNotPresent(taskTemplates, task);
  });

  group.taskTemplateIds?.forEach(taskId => {
    pushIfNotPresent(taskTemplateIds, taskId);
    const task = taskTemplatesById[taskId];
    if (task) {
      if (!taskTemplates.some(t => t.id === taskId)) {
        taskTemplates.push(task);
      }
    }
  });

  return {
    taskTemplateIds,
    taskTemplates: orderTasksByTemplateIds(group, taskTemplates),
  };
};

const orderTasksByTemplateIds = (group: ScanTemplateGroup, taskTemplates: ScanTemplateTask[]): ScanTemplateTask[] => {
  if (!group.taskTemplateIds || group.taskTemplateIds.length === 0) {
    return taskTemplates;
  }

  const orderedTaskTemplates: ScanTemplateTask[] = [];

  group.taskTemplateIds.forEach(taskId => {
    const task = taskTemplates.find(t => t.id === taskId);
    if (task) {
      orderedTaskTemplates.push(task);
    }
  });

  taskTemplates.forEach(task => {
    if (!group.taskTemplateIds?.includes(task.id)) {
      orderedTaskTemplates.push(task);
    }
  });

  return orderedTaskTemplates;
};

const getTaskTemplatesById = (template: ScanTemplateRoot): Record<string, ScanTemplateTask> => ({ ...(template.taskTemplatesById ?? {}) });

const createNormalizedGroup = (group: ScanTemplateGroup, taskTemplatesById: Record<string, ScanTemplateTask>) => ({
  ...group,
  taskTemplates: undefined,
  taskTemplateIds: dropEmptyArrays(extractGroupTasks(group, taskTemplatesById).taskTemplateIds),
});

const createDenormalizedGroup = (group: ScanTemplateGroup, taskTemplatesById: Record<string, ScanTemplateTask>) => ({
  ...group,
  taskTemplates: dropEmptyArrays(extractGroupTasks(group, taskTemplatesById).taskTemplates),
  taskTemplateIds: undefined,
});

/**
 * Converts a scan template to normalized form by deduplicating tasks across groups.
 *
 * Normalized scan templates have:
 * - All unique tasks stored once in `taskTemplatesById` map
 * - Groups reference tasks by ID using `taskTemplateIds` array
 * - Groups do not contain inline `taskTemplates`
 *
 * This transformation:
 * - Collects all tasks from all groups into a global `taskTemplatesById` map
 * - Converts each group's `taskTemplates` to `taskTemplateIds` references
 * - Handles partially normalized templates (mix of inline and referenced tasks)
 * - Validates that all referenced tasks exist in the map
 * - Preserves all other template properties
 *
 * @param template - The scan template to normalize (can be denormalized or partially normalized)
 * @returns A normalized scan template with deduplicated tasks
 * @throws {Error} When a task is referenced in `taskTemplateIds` but not found in `taskTemplatesById`
 *
 * @example
 * ```typescript
 * // Denormalized template with duplicated tasks
 * const denormalized = {
 *   groupTemplates: [
 *     { taskTemplates: [task1, task2] },
 *     { taskTemplates: [task1, task3] } // task1 duplicated
 *   ]
 * }
 *
 * // Normalized template with deduplicated tasks
 * const normalized = getNormalizedScanTemplateRoot(denormalized)
 * // Result:
 * // {
 * //   taskTemplatesById: { "task1": task1, "task2": task2, "task3": task3 },
 * //   groupTemplates: [
 * //     { taskTemplateIds: ["task1", "task2"] },
 * //     { taskTemplateIds: ["task1", "task3"] }
 * //   ]
 * // }
 * ```
 */
export const getNormalizedScanTemplateRoot = (template: ScanTemplateRoot): ScanTemplateRoot => {
  if (isTemplateNormalized(template)) return template;
  const taskTemplatesById = getTaskTemplatesById(template);

  template.groupTemplates?.forEach(group => {
    collectInlineTasks(group, taskTemplatesById);
    validateReferencedTasks(group, taskTemplatesById);
  });

  return {
    ...template,
    taskTemplatesById: dropEmptyObjects(taskTemplatesById),
    groupTemplates: template.groupTemplates?.map(group => createNormalizedGroup(group, taskTemplatesById)),
  };
};

/**
 * Converts a scan template to denormalized form by expanding task references into inline tasks.
 *
 * Denormalized scan templates have:
 * - All tasks duplicated in each group's `taskTemplates` array
 * - Groups do not use `taskTemplateIds` references
 * - No global `taskTemplatesById` map
 *
 * This transformation:
 * - Expands all task references from `taskTemplateIds` into inline `taskTemplates`
 * - Preserves the original order of tasks from `taskTemplateIds`
 * - Handles partially denormalized templates (mix of inline and referenced tasks)
 * - Removes the global `taskTemplatesById` map
 * - Preserves all other template properties
 *
 * @param template - The scan template to denormalize (can be normalized or partially denormalized)
 * @returns A denormalized scan template with duplicated tasks
 *
 * @example
 * ```typescript
 * // Normalized template with task references
 * const normalized = {
 *   taskTemplatesById: { "task1": task1, "task2": task2, "task3": task3 },
 *   groupTemplates: [
 *     { taskTemplateIds: ["task1", "task2"] },
 *     { taskTemplateIds: ["task1", "task3"] }
 *   ]
 * }
 *
 * // Denormalized template with duplicated tasks
 * const denormalized = getDenormalizedScanTemplateData(normalized)
 * // Result:
 * // {
 * //   groupTemplates: [
 * //     { taskTemplates: [task1, task2] },
 * //     { taskTemplates: [task1, task3] } // task1 duplicated
 * //   ]
 * // }
 * ```
 */
export const getDenormalizedScanTemplateData = (template: ScanTemplateRoot): ScanTemplateRoot => {
  if (isTemplateDenormalized(template)) return template;
  const taskTemplatesById = getTaskTemplatesById(template);
  return ({
    ...template,
    taskTemplatesById: undefined,
    groupTemplates: template.groupTemplates?.map(group => createDenormalizedGroup(group, taskTemplatesById)),
  });
};

/**
 * Quickly checks if a scan template is in normalized form by examining group structure.
 *
 * A template is considered normalized iff it contains NO denormalized data. Specifically:
 * - No group has `taskTemplates`
 *
 * Technically a template can be both normalized and denormalized IFF there are no tasks at all in the template.
 *
 * @param template - The scan template to check
 * @returns true if the template is normalized, false otherwise
 */
export const isTemplateNormalized = (template: ScanTemplateRoot): boolean =>
  template.groupTemplates?.every(group => group.taskTemplates === undefined) ?? false;

/**
 * Quickly checks if a scan template is in denormalized form by examining group structure.
 *
 * A template is considered denormalized iff it contains NO normalized data. Specifically:
 * - The root `taskTemplatesById` is undefined
 *
 * Technically a template can be both normalized and denormalized IFF there are no tasks at all in the template.
 *
 * @param template - The scan template to check
 * @returns true if the template is denormalized, false otherwise
 */
export const isTemplateDenormalized = (template: ScanTemplateRoot): boolean => !template.taskTemplatesById;
